import React, { useMemo, useRef, useState, useEffect } from 'react';
import { NodeStats } from '@/types';
import { formatSmallAmount, formatHashPower, getTierName } from '@/utils/format';
import '@/styles/effects.css';

interface NodeTreeMapProps {
  nodes: NodeStats[];
  onNodeClick: (node: NodeStats) => void;
  selectedNode: NodeStats | null;
  pendingRewards?: { [key: string]: number };
}

interface PositionedNode {
  node: NodeStats;
  x: number;
  y: number;
  radius: number;
  color: string;
  angle: number;
  distance: number;
}

const VIEW_W = 1000;
const VIEW_H = 700;
const CENTER = { x: VIEW_W / 2, y: VIEW_H / 2 };

// ETC identity palette only (no purple / no ETH-blue)
const TIER_COLORS: Record<number, string> = {
  1: '#64748b', // Common  - slate/gray
  2: '#00ff88', // Uncommon- neon green
  3: '#14b8a6', // Rare    - teal
  4: '#fbbf24', // Epic    - amber
  5: '#fbbf24', // Legendary- amber (brighter via glow)
};

const tierColor = (tier: number): string => TIER_COLORS[tier] || '#00ff88';

export const NodeTreeMap: React.FC<NodeTreeMapProps> = ({
  nodes,
  onNodeClick,
  selectedNode,
  pendingRewards = {},
}) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const dragOrigin = useRef({ x: 0, y: 0, panX: 0, panY: 0, moved: false });
  const containerRef = useRef<HTMLDivElement>(null);

  const nowSec = Math.floor(Date.now() / 1000);

  // A node is considered "expired" if it has a deactivation timestamp in the past.
  const isExpired = (node: NodeStats): boolean =>
    !!node.deactivationTime && node.deactivationTime > 0 && node.deactivationTime < nowSec;

  // Build a radial topology: COMMAND CORE hub in the centre, player nodes in rings around it.
  const positioned = useMemo<PositionedNode[]>(() => {
    if (nodes.length === 0) return [];

    const sorted = [...nodes].sort((a, b) => {
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;
      if (a.tier !== b.tier) return b.tier - a.tier;
      return parseInt(a.tokenId, 10) - parseInt(b.tokenId, 10);
    });

    const capacities = [6, 12, 18, 24, 30, 36];
    const result: PositionedNode[] = [];

    let index = 0;
    for (const node of sorted) {
      // Determine ring + position within ring.
      let ring = 0;
      let start = 0;
      while (
        ring < capacities.length - 1 &&
        index >= start + capacities[ring]
      ) {
        start += capacities[ring];
        ring += 1;
      }
      const idxInRing = index - start;
      const cap = capacities[Math.min(ring, capacities.length - 1)];
      const countInRing = Math.min(cap, sorted.length - start);
      const radius = 135 + ring * 95;
      // Offset alternating rings so nodes don't line up radially.
      const ringOffset = ring % 2 === 1 ? Math.PI / Math.max(1, countInRing) : 0;
      const angle =
        (idxInRing / Math.max(1, countInRing)) * Math.PI * 2 -
        Math.PI / 2 +
        ringOffset;

      const nodeRadius = 15 + Math.min(node.tier, 5) * 3.2;

      result.push({
        node,
        x: CENTER.x + Math.cos(angle) * radius,
        y: CENTER.y + Math.sin(angle) * radius,
        radius: nodeRadius,
        color: tierColor(node.tier),
        angle,
        distance: radius,
      });
      index += 1;
    }
    return result;
  }, [nodes]);

  const activeCount = nodes.filter((n) => n.isActive).length;

  // ---- Zoom + pan (preserve original interaction functionality) ----
  const clampZoom = (z: number) => Math.max(0.5, Math.min(3, z));

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      setZoom((z) => clampZoom(z * (e.deltaY > 0 ? 0.9 : 1.1)));
    };
    container.addEventListener('wheel', onWheel, { passive: false });
    return () => container.removeEventListener('wheel', onWheel);
  }, []);

  const toSvgScale = () => {
    const width = containerRef.current?.clientWidth || VIEW_W;
    return VIEW_W / width;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    dragOrigin.current = {
      x: e.clientX,
      y: e.clientY,
      panX: pan.x,
      panY: pan.y,
      moved: false,
    };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    const scale = toSvgScale();
    const dx = (e.clientX - dragOrigin.current.x) * scale;
    const dy = (e.clientY - dragOrigin.current.y) * scale;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) dragOrigin.current.moved = true;
    setPan({ x: dragOrigin.current.panX + dx, y: dragOrigin.current.panY + dy });
  };

  const endDrag = () => setIsDragging(false);

  const resetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleNodeActivate = (node: NodeStats) => {
    // Ignore click that was actually a drag.
    if (dragOrigin.current.moved) return;
    onNodeClick(node);
  };

  const groupTransform = `translate(${CENTER.x + pan.x} ${CENTER.y + pan.y}) scale(${zoom}) translate(${-CENTER.x} ${-CENTER.y})`;

  const hoveredNode = positioned.find((p) => p.node.tokenId === hoveredId) || null;

  return (
    <div
      ref={containerRef}
      className={`netmap ${isDragging ? 'is-dragging' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={endDrag}
      onMouseLeave={endDrag}
    >
      <div className="netmap-controls">
        <button
          type="button"
          className="netmap-ctrl-btn"
          onClick={() => setZoom((z) => clampZoom(z * 1.2))}
          aria-label="Zoom in"
        >
          +
        </button>
        <button
          type="button"
          className="netmap-ctrl-btn"
          onClick={() => setZoom((z) => clampZoom(z * 0.8))}
          aria-label="Zoom out"
        >
          −
        </button>
        <button
          type="button"
          className="netmap-ctrl-btn"
          onClick={resetView}
          aria-label="Reset view"
        >
          ⟳
        </button>
        <div className="netmap-zoom">{Math.round(zoom * 100)}%</div>
      </div>

      {positioned.length === 0 ? (
        <EmptyState />
      ) : (
        <svg
          className="netmap-svg"
          viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
          preserveAspectRatio="xMidYMid meet"
        >
          <defs>
            <radialGradient id="netmap-core-grad" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#7dffc2" />
              <stop offset="55%" stopColor="#00ff88" />
              <stop offset="100%" stopColor="#04140f" />
            </radialGradient>
            <radialGradient id="netmap-vignette" cx="50%" cy="50%" r="65%">
              <stop offset="0%" stopColor="rgba(0,255,136,0.05)" />
              <stop offset="100%" stopColor="rgba(2,8,6,0)" />
            </radialGradient>
            <pattern
              id="netmap-grid"
              width="40"
              height="40"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="rgba(0,255,136,0.06)"
                strokeWidth="1"
              />
            </pattern>
            <filter id="netmap-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#netmap-grid)" />
          <rect x="0" y="0" width={VIEW_W} height={VIEW_H} fill="url(#netmap-vignette)" />

          <g transform={groupTransform}>
            {/* Orbit guide rings */}
            {[135, 230, 325, 420].map((r) => (
              <circle
                key={`orbit-${r}`}
                cx={CENTER.x}
                cy={CENTER.y}
                r={r}
                className="netmap-orbit"
              />
            ))}

            {/* Connections hub -> node */}
            {positioned.map((p, i) => {
              const active = p.node.isActive;
              const path = `M ${CENTER.x} ${CENTER.y} L ${p.x} ${p.y}`;
              return (
                <g key={`link-${p.node.tokenId}`}>
                  <line
                    x1={CENTER.x}
                    y1={CENTER.y}
                    x2={p.x}
                    y2={p.y}
                    className={`netmap-link ${active ? 'is-active' : 'is-inactive'}`}
                    style={active ? { stroke: p.color } : undefined}
                  />
                  {active && (
                    <>
                      <line
                        x1={CENTER.x}
                        y1={CENTER.y}
                        x2={p.x}
                        y2={p.y}
                        className="netmap-link-flow"
                        style={{ stroke: p.color }}
                      />
                      <circle r={2.6} className="netmap-packet" style={{ fill: p.color }}>
                        <animateMotion
                          dur={`${2 + (i % 3) * 0.4}s`}
                          repeatCount="indefinite"
                          path={path}
                          begin={`${(i % 5) * 0.3}s`}
                        />
                      </circle>
                    </>
                  )}
                </g>
              );
            })}

            {/* Central COMMAND CORE hub */}
            <g className="netmap-core">
              <circle
                cx={CENTER.x}
                cy={CENTER.y}
                r={62}
                className="netmap-core-ring-outer"
              />
              <circle
                cx={CENTER.x}
                cy={CENTER.y}
                r={48}
                className="netmap-core-ring-inner"
              />
              <circle
                cx={CENTER.x}
                cy={CENTER.y}
                r={34}
                fill="url(#netmap-core-grad)"
                filter="url(#netmap-glow)"
                className="netmap-core-body"
              />
              <text
                x={CENTER.x}
                y={CENTER.y - 2}
                className="netmap-core-label"
                textAnchor="middle"
              >
                COMMAND
              </text>
              <text
                x={CENTER.x}
                y={CENTER.y + 12}
                className="netmap-core-label"
                textAnchor="middle"
              >
                CORE
              </text>
            </g>

            {/* Nodes */}
            {positioned.map((p, i) => {
              const active = p.node.isActive;
              const expired = isExpired(p.node);
              const selected =
                selectedNode?.tokenId === p.node.tokenId;
              const hovered = hoveredId === p.node.tokenId;
              const stateClass = active
                ? 'is-active'
                : expired
                ? 'is-expired'
                : 'is-inactive';

              return (
                <g
                  key={`node-${p.node.tokenId}`}
                  className={`netmap-node ${stateClass} ${selected ? 'is-selected' : ''} ${hovered ? 'is-hovered' : ''}`}
                  transform={`translate(${p.x} ${p.y})`}
                  onMouseEnter={() => setHoveredId(p.node.tokenId)}
                  onMouseLeave={() =>
                    setHoveredId((cur) => (cur === p.node.tokenId ? null : cur))
                  }
                  onClick={() => handleNodeActivate(p.node)}
                >
                  <g
                    className="netmap-node-fx"
                    style={{ animationDelay: `${Math.min(i * 40, 800)}ms` }}
                  >
                    {/* Selection glow ring */}
                    {selected && (
                      <circle r={p.radius + 12} className="netmap-select-ring" />
                    )}
                    {/* Expired amber ring */}
                    {expired && !active && (
                      <circle r={p.radius + 7} className="netmap-expired-ring" />
                    )}
                    {/* Active pulse */}
                    {active && (
                      <circle
                        r={p.radius}
                        className="netmap-pulse"
                        style={{ stroke: p.color }}
                      />
                    )}
                    <circle
                      r={p.radius}
                      className="netmap-node-body"
                      style={{
                        fill: active ? p.color : '#1b2a26',
                        stroke: active ? p.color : 'rgba(125,255,194,0.25)',
                      }}
                      filter={active ? 'url(#netmap-glow)' : undefined}
                    />
                    <text className="netmap-node-tier" textAnchor="middle" dy="0.35em">
                      {p.node.tier}
                    </text>
                    <text
                      className="netmap-node-id"
                      textAnchor="middle"
                      y={p.radius + 15}
                    >
                      #{p.node.tokenId}
                    </text>
                  </g>
                </g>
              );
            })}

            {/* Tooltip */}
            {hoveredNode && (
              <foreignObject
                x={Math.max(8, Math.min(hoveredNode.x - 105, VIEW_W - 218))}
                y={
                  hoveredNode.y - hoveredNode.radius - 132 > 6
                    ? hoveredNode.y - hoveredNode.radius - 132
                    : hoveredNode.y + hoveredNode.radius + 12
                }
                width={210}
                height={130}
                style={{ overflow: 'visible', pointerEvents: 'none' }}
              >
                <div className="netmap-tooltip">
                  <div
                    className="netmap-tt-head"
                    style={{ color: hoveredNode.color }}
                  >
                    <span>NODE-{hoveredNode.node.tokenId}</span>
                    <span className="netmap-tt-state">
                      {hoveredNode.node.isActive
                        ? 'ACTIVE'
                        : isExpired(hoveredNode.node)
                        ? 'EXPIRED'
                        : 'OFFLINE'}
                    </span>
                  </div>
                  <div className="netmap-tt-row">
                    <span>TIER</span>
                    <span>
                      {hoveredNode.node.tier} · {getTierName(hoveredNode.node.tier)}
                    </span>
                  </div>
                  <div className="netmap-tt-row">
                    <span>HASH</span>
                    <span>{formatHashPower(hoveredNode.node.hashPower)}</span>
                  </div>
                  <div className="netmap-tt-row">
                    <span>BAND</span>
                    <span>{hoveredNode.node.bandwidth} KB/s</span>
                  </div>
                  <div className="netmap-tt-row">
                    <span>UPTIME</span>
                    <span>{hoveredNode.node.uptime}%</span>
                  </div>
                  <div className="netmap-tt-row netmap-tt-pending">
                    <span>PENDING</span>
                    <span>
                      {formatSmallAmount(pendingRewards[hoveredNode.node.tokenId] || 0)}
                    </span>
                  </div>
                </div>
              </foreignObject>
            )}
          </g>
        </svg>
      )}

      {/* Legend */}
      <div className="netmap-legend">
        <span className="netmap-legend-item">
          <i className="netmap-dot" style={{ background: '#00ff88' }} /> ACTIVE {activeCount}
        </span>
        <span className="netmap-legend-item">
          <i className="netmap-dot netmap-dot-off" /> OFFLINE {nodes.length - activeCount}
        </span>
      </div>
    </div>
  );
};

const EmptyState: React.FC = () => (
  <div className="netmap-empty">
    <div className="netmap-radar">
      <span className="netmap-radar-ring" />
      <span className="netmap-radar-ring netmap-radar-ring-2" />
      <span className="netmap-radar-ring netmap-radar-ring-3" />
      <span className="netmap-radar-sweep" />
      <span className="netmap-radar-core" />
    </div>
    <div className="netmap-empty-title">NO NODES DEPLOYED</div>
    <div className="netmap-empty-sub">Open Shop to buy a starter node and light up the network</div>
  </div>
);
